import { CornerTable } from "../../../v2/components/CornerTable.jsx";
import { budgetHealth } from "../../../lib/projectDisplaySignals.js";
import { BudgetHealthLabel, budgetHealthColor } from "../ProjectDisplaySignals.jsx";
import { ActualsThrough } from "./ActualsThrough.jsx";
// Drawer-only Financials presentation (Task #9168). Rendered by
// ProjectBudgetSection when variant="drawer"; that owner keeps the data,
// mutations, confirmations and editor busy/dirty reporting.
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, LAYERS } from "../../../app/tokens.js";
import { useFxRates } from "../../../data/useFxRates.js";
import { useSensitivityPolicy } from "../../../contexts/SensitivityPolicyContext.jsx";
import {
  projectDrawerFinancials, resolveDisplayCurrency, CATEGORY_SOURCE_TEXT,
  formatCompact, formatExact, formatPct, FIN_FIELDS,
} from "./drawerFinancialsModel.js";

const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };
const eyebrow = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: T.mid };

// Floating layers are portalled out of the table's overflow wrapper into the
// record drawer (inside its focus trap) or document.body when standalone, and
// positioned fixed, clamped to the viewport. The drawer sheet sits one layer
// above sticky chrome; these layers stay above it without changing shared UI.
export const FLOAT_Z = LAYERS.stickyChrome + 2;
const GAP = 6;
const EDGE = 8;

function hostFor(el) {
  return el?.closest?.('[data-testid="project-record-drawer"]') || (typeof document !== "undefined" ? document.body : null);
}

/** Fixed coordinates for `layer` next to `anchor`, clamped to the viewport.
 *  Compensates when the host is transformed (fixed then resolves to the host). */
function placeLayer(anchor, layer, host, { prefer = "above", align = "center" } = {}) {
  if (!anchor || !layer) return null;
  const a = anchor.getBoundingClientRect();
  const w = layer.offsetWidth || 0; const h = layer.offsetHeight || 0;
  const vw = window.innerWidth; const vh = window.innerHeight;
  let left = align === "right" ? a.right - w : align === "left" ? a.left : a.left + a.width / 2 - w / 2;
  left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
  let top = prefer === "above" ? a.top - h - GAP : a.bottom + GAP;
  if (prefer === "above" && top < EDGE) top = a.bottom + GAP;
  if (prefer === "below" && top + h > vh - EDGE) top = Math.max(EDGE, a.top - h - GAP);
  top = Math.max(EDGE, Math.min(top, vh - h - EDGE));
  if (host && host !== document.body) {
    const cs = window.getComputedStyle(host);
    if ((cs.transform && cs.transform !== "none") || (cs.filter && cs.filter !== "none")) {
      const r = host.getBoundingClientRect();
      left -= r.left; top -= r.top;
    }
  }
  return { left, top };
}

function useFloating(open, anchorRef, layerRef, host, opts) {
  const [pos, setPos] = useState(null);
  const optsRef = useRef(opts); optsRef.current = opts;
  const update = useCallback(() => setPos(placeLayer(anchorRef.current, layerRef.current, host, optsRef.current)), [anchorRef, layerRef, host]);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [open, update]);
  return pos;
}

function useHost(anchorRef) {
  const [host, setHost] = useState(null);
  useLayoutEffect(() => { setHost(hostFor(anchorRef.current)); }, [anchorRef]);
  return host;
}

const tipStyle = { background: T.tooltip.bg, color: T.white, fontSize: T.fsXs, fontWeight: T.fwSemibold, lineHeight: 1.45, padding: "7px 10px", borderRadius: 6, width: "max-content", maxWidth: "min(300px, calc(100vw - 16px))", maxHeight: "min(260px, calc(100vh - 16px))", overflowY: "auto", whiteSpace: "pre-line", overflowWrap: "anywhere", textAlign: "left", textTransform: "none", letterSpacing: 0, boxShadow: `0 6px 18px ${T.borderDark}` };

/** Accessible tooltip: hover/focus open, click toggles, Escape dismisses until the next entry. */
export function Tip({ content, children, testId, label, align = "center", as = "button" }) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const id = useId();
  const anchor = useRef(null);
  const layer = useRef(null);
  const host = useHost(anchor);
  const open = !dismissed && (hover || focus || pinned);
  const pos = useFloating(open, anchor, layer, host, { prefer: "above", align });
  const Trigger = as;
  const triggerProps = as === "button"
    ? { type: "button", "aria-label": label, "aria-expanded": open, style: { border: 0, background: "none", padding: 0, margin: "0 0 0 4px", cursor: "help", color: T.light, display: "inline-flex", verticalAlign: "middle" } }
    : { tabIndex: 0, role: "button", "aria-expanded": open, style: { cursor: "help", outlineOffset: 2 } };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setDismissed(true); setPinned(false); } };
    const onDown = (e) => { if (!anchor.current?.contains(e.target) && !layer.current?.contains(e.target)) setPinned(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [open]);
  const bubble = (
    <span ref={layer} role="tooltip" id={id} data-testid={testId ? `${testId}-tip` : undefined} data-open={open ? "true" : "false"}
      tabIndex={open ? 0 : undefined}
      onFocus={() => setFocus(true)}
      onBlur={e => { if (!anchor.current?.contains(e.relatedTarget)) { setFocus(false); setPinned(false); } }}
      style={open ? { position: "fixed", left: pos?.left ?? -9999, top: pos?.top ?? -9999, zIndex: FLOAT_Z, ...tipStyle } : srOnly}>
      {content}
    </span>
  );
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => { setDismissed(false); setHover(true); }} onMouseLeave={() => setHover(false)}>
      <Trigger {...triggerProps} ref={anchor} data-testid={testId} aria-describedby={id}
        onFocus={() => { setDismissed(false); setFocus(true); }} onBlur={e => { if (!layer.current?.contains(e.relatedTarget)) { setFocus(false); setPinned(false); } }}
        onClick={() => { if (dismissed || !open) { setDismissed(false); setPinned(true); } else { setPinned(false); setHover(false); setFocus(false); setDismissed(true); } }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setDismissed(true); setPinned(false); }
          else if (as !== "button" && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); e.currentTarget.click(); }
        }}>
        {children ?? <InfoGlyph />}
      </Trigger>
      {host ? createPortal(bubble, host) : bubble}
    </span>
  );
}

function InfoGlyph() {
  return (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="8" cy="8" r="6.6" /><path d="M8 7.2v4.2" strokeLinecap="round" /><circle cx="8" cy="4.9" r=".55" fill="currentColor" stroke="none" />
    </svg>
  );
}

function Money({ value, currency, signed, testId, color, weight, size, reason }) {
  return (
    <Tip as="span" align="right" testId={testId} content={value == null && reason ? reason : formatExact(value, currency)}>
      <span data-testid={testId ? `${testId}-value` : undefined} style={{ color, fontWeight: weight, fontSize: size, fontVariantNumeric: "tabular-nums" }}>
        {formatCompact(value, currency, { signed })}
      </span>
    </Tip>
  );
}

const varColor = (v, budget, forecast) => budgetHealthColor(budgetHealth(budget, forecast));

/** Keyboard/pointer row menu: Enter/Space/ArrowDown open, arrows move,
 *  Escape returns focus, Tab and focus/pointer outside close without trapping. */
export function RowMenu({ label, items, testId, disabled }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const menu = useRef(null);
  const refs = useRef([]);
  const host = useHost(btn);
  const pos = useFloating(open, btn, menu, host, { prefer: "below", align: "right" });
  const inside = (t) => btn.current?.contains(t) || menu.current?.contains(t);
  useEffect(() => {
    if (!open) return undefined;
    refs.current.find((el) => el && !el.disabled)?.focus();
    const away = (e) => { if (!inside(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("focusin", away);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("focusin", away); };
  }, [open]);
  const close = () => { setOpen(false); btn.current?.focus(); };
  const onMenuKey = (e) => {
    const els = refs.current.filter((el) => el && !el.disabled);
    const i = els.indexOf(document.activeElement);
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === "Tab") {
      // Leave the menu: return to the trigger so natural tab order resumes from the row.
      e.preventDefault(); setOpen(false);
      const order = [...(hostFor(btn.current) || document).querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => !menu.current?.contains(el));
      const at = order.indexOf(btn.current);
      (order[at + (e.shiftKey ? 0 : 1)] || btn.current)?.focus();
    }
    else if (e.key === "ArrowDown") { e.preventDefault(); els[(i + 1) % els.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); els[(i - 1 + els.length) % els.length]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); els[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); els[els.length - 1]?.focus(); }
  };
  const layer = open && (
    <div ref={menu} role="menu" aria-label={label} onKeyDown={onMenuKey} data-testid={`${testId}-menu`}
      style={{ position: "fixed", left: pos?.left ?? -9999, top: pos?.top ?? -9999, zIndex: FLOAT_Z, minWidth: 120, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 4, boxShadow: `0 8px 22px ${T.borderDark}` }}>
      {items.map((it, i) => (
        <button key={it.key} ref={(el) => { refs.current[i] = el; }} type="button" role="menuitem" data-testid={`${testId}-${it.key}`} disabled={it.disabled}
          onClick={() => { setOpen(false); btn.current?.focus(); it.onSelect(); }}
          style={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "none", borderRadius: 5, padding: "6px 9px", fontSize: T.fsSm, color: it.danger ? T.red : T.dark, cursor: it.disabled ? "default" : "pointer", opacity: it.disabled ? 0.5 : 1 }}>
          {it.label}
        </button>
      ))}
    </div>
  );
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button ref={btn} type="button" data-testid={testId} aria-label={label} aria-haspopup="menu" aria-expanded={open} disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } }}
        className="dfin-rowact"
        style={{ border: 0, background: "none", borderRadius: 5, padding: "2px 6px", cursor: disabled ? "default" : "pointer", color: T.light, fontWeight: T.fwBold, fontSize: T.fsBase, lineHeight: 1 }}>
        <span aria-hidden="true">...</span>
      </button>
      {layer && (host ? createPortal(layer, host) : layer)}
    </span>
  );
}

const NATIVE_LABEL = { budget: "Budget", committed: "Committed", forecast: "Forecast", invoiced: "Invoiced" };

/** Always discloses source currency and exact native amounts, plus notes. */
export function rowTip(row) {
  const lines = [];
  lines.push(`Source currency: ${row.currency || "not recorded"}`);
  for (const k of FIN_FIELDS) {
    const v = row.native[k];
    lines.push(`${NATIVE_LABEL[k]}: ${v == null ? "not recorded" : `${formatExact(v, row.currency)}`}`);
  }
  if (row.costRsf != null) lines.push(`Cost / RSF: ${row.costRsf.toLocaleString("en-US", { maximumFractionDigits: 2 })}${row.currency ? ` ${row.currency}` : ""}`);
  if (row.note) lines.push(`Note: ${row.note}`);
  if (row.converted) lines.push("Displayed values are converted once from the source currency.");
  if (row.issue === "missing-currency") lines.push("Row currency is not recorded, so this row cannot be converted and totals are withheld.");
  if (row.issue === "missing-rate") lines.push(`No exchange rate is available for ${row.currency}; this row is not converted and totals are withheld.`);
  if (row.issue === "missing-display") lines.push("No display currency is recorded for this project; totals are withheld.");
  return lines.join("\n");
}

function Summary({ fin }) {
  const ccy = fin.displayCurrency;
  const s = fin.summary;
  const tiles = [
    ["budget", "Budget", s.budget, "approved", false],
    ["forecast", "Forecast", s.forecast, "at completion", false],
    ["variance", "Variance", s.variance, "budget − forecast", true],
    ["spent", fin.actuals.includesPlan ? "Spend (includes plan)" : "Spent (actuals)", s.spent, s.spentPctOfForecast == null ? null : `${formatPct(s.spentPctOfForecast)} of forecast`, false],
    ["remaining", "Remaining", s.remaining, fin.actuals.includesPlan ? "forecast − spend (includes plan)" : "forecast − actuals", false],
  ];
  return (
    <div data-testid="dfin-summary" className="dfin-strip">
      {tiles.map(([key, label, value, sub, signed]) => (
        <div key={key} data-testid={`dfin-tile-${key}`} style={{ minWidth: 0, padding: "10px 12px", borderRadius: 10, background: T.card, border: `1px solid ${T.border}` }}>
          <div style={{ ...eyebrow, display: "flex", alignItems: "center" }}>
            {label}
            {key === "spent" && fin.actuals.source && <Tip label="Source of spent actuals" testId="dfin-spent-source" content={`${fin.actuals.source.label}\n${fin.actuals.source.disclosure || ""}\nAs-of cutoff: ${fin.actuals.asOfDate}\nNative actuals: ${formatExact(fin.actuals.native, fin.actuals.currency)}\nCategory Invoiced is a separate measure, not actuals.`} />}
            {key === "spent" && fin.actuals.provenanceUnavailable && <Tip label="Actuals provenance unavailable" testId="dfin-spent-provenance" content={fin.actuals.reason} />}
          </div>
          <div style={{ marginTop: 3 }}>
            {key === "spent" && fin.actuals.status === "missing" && !fin.untracked
              ? <span data-testid="dfin-spent-empty" style={{ fontSize: T.fsXs, color: T.mid }}>No actuals recorded</span>
              : <Money value={value} currency={ccy} signed={signed} testId={`dfin-${key}`} size={T.fsLg} weight={T.fwExtrabold}
                reason={fin.untracked ? "Not financially tracked." : fin.headlineIssues[key]}
                color={key === "variance" ? varColor(value, s.budget, s.forecast) : value == null ? T.light : T.dark} />}
            {key === "variance" && <> · <BudgetHealthLabel budget={s.budget} forecast={s.forecast} currency={ccy} /></>}
          </div>
          <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>{key === "spent" && fin.actuals.status === "unavailable" ? "Display unavailable" : sub}</div>
          {key === "spent" && value != null && <ActualsThrough date={fin.actuals.asOfDate} includesPlan={fin.actuals.includesPlan} />}
        </div>
      ))}
    </div>
  );
}

const SEG_COLOR = { spent: T.dark, remaining: T.mid, contingency: T.light, headroom: T.border };

function CompositionBar({ fin }) {
  const { bar, displayCurrency: ccy } = fin;
  if (fin.actuals.includesPlan) return <div role="note" style={{ marginTop: 10, fontSize: T.fsXs, color: T.mid }}>Spend includes plan; an actuals composition is unavailable.</div>;
  if (bar.status !== "ok") {
    return <div data-testid="dfin-bar-unavailable" role="note" style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, border: `1px dashed ${T.borderDark}`, fontSize: T.fsXs, color: T.mid }}>{bar.reason}</div>;
  }
  return (
    <figure data-testid="dfin-bar" style={{ margin: "12px 0 0" }} aria-label="Forecast composition">
      <div role="img" aria-label={`Forecast composition: ${bar.segments.map((g) => `${g.label} ${formatExact(g.value, ccy)}`).join(", ")}`}
        style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: T.lightGray }}>
        {bar.segments.map((g) => g.share > 0 && <span key={g.key} data-testid={`dfin-seg-${g.key}`} style={{ width: `${g.share * 100}%`, background: SEG_COLOR[g.key] }} />)}
      </div>
      <figcaption style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 7, fontSize: T.fsXs, color: T.mid }}>
        {bar.segments.map((g) => (
          <span key={g.key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: SEG_COLOR[g.key] }} />
            {g.label} <Money value={g.value} currency={ccy} testId={`dfin-legend-${g.key}`} color={T.dark} weight={T.fwBold} />
          </span>
        ))}
        {bar.overBudget != null && <span style={{ color: budgetHealthColor(budgetHealth(fin.summary.budget, fin.summary.forecast)) }}>Over budget <Money value={bar.overBudget} currency={ccy} testId="dfin-over-budget" color={budgetHealthColor(budgetHealth(fin.summary.budget, fin.summary.forecast))} weight={T.fwBold} /></span>}
      </figcaption>
    </figure>
  );
}

function RatioCell({ r }) {
  if (r == null || budgetHealth(1, r).ratio == null) return <span style={{ color: T.mid }}>Not tracked</span>;
  const over = r > 1;
  const health = budgetHealth(1, r);
  return (
    <span data-testid="dfin-ratio" data-over={over ? "true" : "false"} title={health.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end", color: budgetHealthColor(health), fontWeight: over ? T.fwBold : T.fwSemibold }}>
      {formatPct(r)}
      <span aria-hidden="true" style={{ width: 44, height: 5, borderRadius: 3, background: T.lightGray, position: "relative", overflow: "hidden" }}>
        <span style={{ position: "absolute", inset: 0, transformOrigin: "left", transform: `scaleX(${Math.max(0, Math.min(r, 1))})`, background: budgetHealthColor(health) }} />
      </span>
    </span>
  );
}

const td = { padding: "8px 6px", fontSize: T.fsSm, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };

function Cells({ v, ccy, showCommitted, testId, weight }) {
  return (
    <>
      <td style={td}><Money value={v.budget} currency={ccy} weight={weight} testId={`${testId}-budget`} color={T.dark} /></td>
      {showCommitted && <td style={td}><Money value={v.committed} currency={ccy} weight={weight} testId={`${testId}-committed`} color={T.dark} /></td>}
      <td style={td}><Money value={v.forecast} currency={ccy} weight={weight} testId={`${testId}-forecast`} color={T.dark} /></td>
      <td style={td}><Money value={v.variance} currency={ccy} signed weight={T.fwBold} testId={`${testId}-variance`} color={varColor(v.variance, v.budget, v.forecast)} /> · <BudgetHealthLabel budget={v.budget} forecast={v.forecast} currency={ccy} /></td>
      <td style={td}><Money value={v.invoiced} currency={ccy} weight={weight} testId={`${testId}-invoiced`} color={T.dark} /></td>
      <td style={td}><RatioCell r={v.ratio} /></td>
    </>
  );
}

function rowValues(row) {
  return row; // converted fields live on the row itself
}

export function DrawerFinancials({ project, budgets, canEdit, canAdd, saving, editingId, onAdd, onEdit, onDelete, editor, notices, error, categoriesReady = true }) {
  const policy = useSensitivityPolicy();
  const visible = policy.canSee("projects.budget");
  if (!visible) return null;
  return <DrawerFinancialsBody {...{ project, budgets, canEdit, canAdd, saving, editingId, onAdd, onEdit, onDelete, editor, notices, error, categoriesReady }} />;
}

function DrawerFinancialsBody({ project, budgets, canEdit, canAdd, saving, editingId, onAdd, onEdit, onDelete, editor, notices, error, categoriesReady }) {
  const { rates, loading: fxLoading, error: fxError } = useFxRates();
  const display = resolveDisplayCurrency(project, budgets);
  const fin = useMemo(() => projectDrawerFinancials(budgets, { displayCurrency: display, rates, actuals: project?.spentActuals, project, categoriesReady }), [budgets, display, rates, project, categoriesReady]);
  const needsFx = fin.issues.includes("missing-rate") || fin.actuals.issue === "missing-rate";
  const ccy = fin.displayCurrency;
  const actions = (row) => canEdit && (
    <RowMenu label={`Actions for ${row.label}`} testId={`dfin-menu-${row.id}`} disabled={saving}
      items={[
        { key: "edit", label: "Edit", onSelect: () => onEdit(row.raw), disabled: saving },
        { key: "delete", label: "Delete", danger: true, onSelect: () => onDelete(row.raw), disabled: saving || editingId === row.id },
      ]} />
  );
  const cols = 6 + (fin.showCommitted ? 1 : 0) + (canEdit ? 1 : 0);
  const head = ["Category", "Budget", ...(fin.showCommitted ? ["Committed"] : []), "Forecast", "Variance", "Invoiced", "Forecast vs budget"];

  const catRow = (row, opts = {}) => {
    const tip = rowTip(row);
    return (
      <tr key={row.id} data-testid={`dfin-row-${row.category}`} className="dfin-tr" style={{ borderBottom: `1px solid ${T.border}` }}>
        <td style={{ ...td, textAlign: "left", color: T.dark, fontWeight: opts.weight || T.fwSemibold, paddingLeft: opts.indent ? 2 : 6 }}>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {row.label}
            <Tip label={`About ${row.label}`} testId={`dfin-info-${row.category}`} align="left" content={tip} />
          </span>
          {opts.caption && <div style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwRegular }}>{opts.caption}</div>}
        </td>
        <Cells v={rowValues(row)} ccy={ccy} showCommitted={fin.showCommitted} testId={`dfin-${row.category}`} />
        {canEdit && <td style={{ ...td, width: 30 }}>{actions(row)}</td>}
      </tr>
    );
  };
  const totalRow = (key, label, v, strong) => (
    <tr key={key} data-testid={`dfin-${key}`} style={{ background: strong ? T.blueSoft : T.hoverBg, borderBottom: `1px solid ${T.border}`, borderTop: strong ? `2px solid ${T.projectsHubNavy}` : undefined }}>
      <td style={{ ...td, textAlign: "left", fontWeight: strong ? T.fwHubTitle : T.fwBold, color: strong ? T.projectsHubNavy : T.dark }}>{label}</td>
      <Cells v={v} ccy={ccy} showCommitted={fin.showCommitted} testId={`dfin-${key}`} weight={strong ? T.fwHubTitle : T.fwBold} />
      {canEdit && <td style={td} />}
    </tr>
  );

  return (
    <div data-testid="dfin-root" className="dfin">
      <style>{`
        .dfin .dfin-strip { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; }
        @media (max-width: 640px) { .dfin .dfin-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        .dfin .dfin-tr:hover { background: ${T.hoverBg}; }
        .dfin .dfin-tr:hover .dfin-rowact, .dfin .dfin-rowact:focus-visible, .dfin .dfin-rowact[aria-expanded="true"] { color: ${T.blue}; background: ${T.blueSoft}; }
      `}</style>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
        <h3 style={{ margin: 0, display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwHubTitle, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy }}>
          Financials
          <Tip label="About these financials" testId="dfin-source-info" align="left"
            content={`Headlines prefer project-record approved Budget, Forecast at completion and Spent to date. CapEx record amounts are USD, ex tax, independently of currency metadata. Variance = Budget minus Forecast; Remaining = Forecast minus actuals. Only projects with no record financial values use an explicitly labeled category-total fallback; missing individual record values stay unavailable. TIA is excluded from category totals. ${CATEGORY_SOURCE_TEXT}`} />
        </h3>
        {ccy && (
          <span data-testid="dfin-currency-note" style={{ fontSize: T.fsXs, color: T.mid, display: "inline-flex", alignItems: "center" }}>
            All values {ccy}
            {fin.anyConverted && <Tip label="About currency conversion" testId="dfin-fx-info" align="right" content={`Category rows and/or sourced actuals are converted once to ${ccy} using the workspace FX basis. Native values remain in information disclosures and existing category editors.`} />}
          </span>
        )}
      </header>

      {needsFx && fxLoading && <div data-testid="dfin-fx-loading" aria-busy="true" style={{ color: T.mid, fontSize: T.fsXs }}>Loading category exchange rates…</div>}
      {(
        <>
          {!fin.totalsAvailable && (
            <div role="alert" data-testid="project-cat-budget-currency-warning" style={{ marginBottom: 8, padding: "7px 10px", borderRadius: 8, background: T.ragAmberBg, color: T.ragAmberText, fontSize: T.fsXs }}>
              Category totals are withheld: {fin.issues.includes("missing-currency") ? "a row has no recorded currency" : fin.issues.includes("missing-display") ? "no display currency is recorded for this project" : `no exchange rate is available to convert every row to ${ccy}${fxError ? " (rates failed to load)" : ""}`}. Rows keep their source currencies.
            </div>
          )}
          <Summary fin={fin} />
          <div data-testid="dfin-headline-basis" style={{ color: T.mid, fontSize: T.fsXxs, marginTop: 6 }}>{fin.headlineBasis}</div>
          {fin.discrepancies.map(item => <div key={item.key} data-testid={`dfin-discrepancy-${item.key}`} style={{ color: T.mid, fontSize: T.fsXs, marginTop: 5 }}>
            Category {item.key} totals {formatCompact(item.category, ccy, { signed: item.key === "variance" })}; headline uses CapEx {item.key} {formatCompact(item.headline, ccy, { signed: item.key === "variance" })}
            <Tip label={`Explain ${item.key} discrepancy`} testId={`dfin-discrepancy-${item.key}-info`} content={`Category ${item.key}: ${formatExact(item.category, ccy)}. Project-record ${item.key}: ${formatExact(item.headline, ccy)}. Compared before rounding in the same currency. These are independent recorded measures; this presentation does not reconcile or change either dataset. Category Invoiced is not actuals.`} />
          </div>)}
          <CompositionBar fin={fin} />
        </>
      )}
      {!categoriesReady ? null : fin.status === "empty" ? (
        <div data-testid="dfin-empty" style={{ padding: "18px 14px", borderRadius: 10, border: `1px dashed ${T.borderDark}`, background: T.hoverBg, textAlign: "center" }}>
          <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.projectsHubNavy }}>No category budget rows yet</div>
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>Budget, forecast and invoiced totals appear once category rows are recorded.</div>
          {canAdd && <button type="button" data-testid="project-cat-budget-edit-btn" onClick={onAdd} disabled={saving} style={addBtn}>+ Add category</button>}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "18px 0 6px" }}>
            <div style={{ ...eyebrow, display: "inline-flex", alignItems: "center", color: T.projectsHubNavy }}>
              By category
              <Tip label="About category totals" testId="dfin-category-info" align="left" content="Subtotal covers Soft, Hard and FF&E. Grand total adds Contingency and Other. TIA remains a separate allowance and is never included." />
            </div>
            {canAdd && <button type="button" data-testid="project-cat-budget-edit-btn" onClick={onAdd} disabled={saving} style={addBtn}>+ Add</button>}
          </div>
          <div style={{ overflowX: "auto" }}>
            <CornerTable data-testid="dfin-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: fin.showCommitted ? 600 : 520 }}>
              <thead>
                <tr>
                  {head.map((h) => <th key={h} scope="col" style={{ ...eyebrow, padding: "6px", textAlign: h === "Category" ? "left" : "right", borderBottom: `1px solid ${T.borderDark}` }}>{h}{h === "Invoiced" && <Tip label="About category invoices" testId="dfin-invoiced-info" content="Invoiced contains the recorded category invoice amounts. These differ from project-record actuals in Spent. They are never substituted for actuals in Remaining or the composition bar. Without actuals, Remaining is unavailable." />}</th>)}
                  {canEdit && <th scope="col" style={{ borderBottom: `1px solid ${T.borderDark}` }}><span style={srOnly}>Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {fin.rows.map((r) => catRow(r))}
                {fin.rows.length > 0 && totalRow("subtotal", "Subtotal", fin.subtotal, false)}
                {fin.contingency.map((r) => catRow(r))}
                {fin.other.map((r) => catRow(r, { caption: "Grand total only" }))}
                {totalRow("grand-total", "Grand total", fin.grandTotal, true)}
                {fin.rows.length === 0 && fin.contingency.length + fin.other.length === 0 && (
                  <tr><td colSpan={cols} style={{ ...td, textAlign: "left", color: T.mid, fontStyle: "italic" }}>No cost category rows; only a separate allowance is recorded.</td></tr>
                )}
              </tbody>
            </CornerTable>
          </div>

          {fin.tia.map((row) => (
            <div key={row.id} data-testid="dfin-tia-card" style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", border: `1px dashed ${T.borderDark}`, borderRadius: 10, padding: "10px 12px", background: T.hoverBg }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...eyebrow, display: "inline-flex", alignItems: "center" }}>
                  TIA · separate allowance
                  <Tip label="About TIA" testId="dfin-tia-info" align="left" content={[rowTip(row), "Status: not recorded as structured data.", "TIA is tracked as a separate allowance from the category row and is not included in any total or the composition bar."].join("\n")} />
                </div>
                <div style={{ marginTop: 2 }}>
                  <Money value={row.budget} currency={ccy} testId="dfin-tia" size={T.fsLg} weight={T.fwExtrabold} color={T.projectsHubNavy} />
                  {row.issue && <span style={{ fontSize: T.fsXs, color: T.mid, marginLeft: 6 }}>Display conversion unavailable; source values in info</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span data-testid="dfin-tia-status" style={{ display: "inline-flex", padding: "1px 8px", borderRadius: 6, fontSize: T.fsXs, fontWeight: T.fwSemibold, background: T.lightGray, color: T.mid }}>Status not recorded</span>
                <span style={{ fontSize: T.fsXs, color: T.mid }}>Not included in Grand total</span>
                {actions(row)}
              </div>
            </div>
          ))}
        </>
      )}
      {editor}
      {notices}
      {error && !editingId && <div role="alert" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

const addBtn = { border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "4px 10px", fontWeight: T.fwSemibold, fontSize: T.fsSm, color: T.blue, cursor: "pointer", marginTop: 0 };
